import React from "react";
import { usePage } from "../hooks/useApi";
import { pageApi } from "../services/api";

const MeetOurFounder: React.FC = () => {
  const { data: page, isLoading, error } = usePage("about");

  // Show loading state
  if (isLoading) {
    return (
      <div className="py-16 bg-white">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mx-auto mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3 mx-auto mb-8"></div>
            <div className="max-w-4xl mx-auto">
              <div className="bg-gray-200 rounded-2xl h-96"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return null; // Fail silently for this section
  }

  // Helper function to get field value with fallback
  const getField = (key: string, fallback: string = ""): string => {
    if (!page) return fallback;
    const fields = pageApi.getPageFields(page);
    return fields[key] || fallback;
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-[1280px] mx-auto px-6 md:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-extrabold text-[#1F2937] mb-6">
            {getField("founder_section_title", "Meet Our Founder")}
          </h2>
          <p className="text-lg text-[#334155] max-w-3xl mx-auto leading-relaxed">
            {getField(
              "founder_section_subtitle",
              "At the heart of Rolleston Homes is a vision driven by passion and expertise. Meet Phil Mackay, the founder who leads our dedicated team in bringing your home dreams to life."
            )}
          </p>
        </div>

        {/* Founder Card */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-[#FAFAFA] to-white rounded-3xl shadow-2xl overflow-hidden hover:shadow-3xl transition-all duration-300 transform hover:-translate-y-2">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
              {/* Image Section */}
              <div className="relative overflow-hidden">
                <img
                  src={getField("founder_image", "/images/founder.jpeg")}
                  alt={getField("founder_name", "Phil Mackay")}
                  className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>
              </div>

              {/* Content Section */}
              <div className="p-8 lg:p-12 flex flex-col justify-center">
                <div className="mb-6">
                  <h3 className="text-3xl md:text-4xl font-bold text-[#1F2937] mb-3">
                    {getField("founder_name", "Phil Mackay")}
                  </h3>
                  <div className="text-xl font-semibold text-[#FF5A1F] mb-6">
                    {getField("founder_role", "Founder & Managing Director")}
                  </div>
                </div>

                <div className="space-y-4 text-[#334155] leading-relaxed">
                  <p>
                    {getField(
                      "founder_bio_1",
                      "With over 15 years of experience in the New Zealand building industry, Phil founded Rolleston Homes with a commitment to quality craftsmanship and personalized service."
                    )}
                  </p>
                  <p>
                    {getField(
                      "founder_bio_2",
                      "His leadership ensures every project reflects our core values and exceeds client expectations, creating homes where families can thrive for generations."
                    )}
                  </p>
                </div>

                {/* Key Achievements */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-[#FF5A1F]">
                        {getField("founder_stat1", "18+")}
                      </div>
                      <div className="text-sm text-[#334155]">
                        {getField("founder_stat1_label", "Years Experience")}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-[#FF5A1F]">
                        {getField("founder_stat2", "500+")}
                      </div>
                      <div className="text-sm text-[#334155]">
                        {getField("founder_stat2_label", "Services Delivered")}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MeetOurFounder;
